import { useState } from "react";
import { useLocation, useParams } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import { getGetMeQueryKey, useAcceptInvite, useGetInvite } from "@workspace/api-client-react";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Building2, Loader2, CheckCircle2, XCircle } from "lucide-react";

export default function AcceptInvite() {
  const { token } = useParams<{ token: string }>();
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [userExists, setUserExists] = useState(false);
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const qc = useQueryClient();

  const { data: inv, isLoading, error: invError } = useGetInvite(token ?? "");

  const acceptInvite = useAcceptInvite();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const result = await acceptInvite.mutateAsync({ data: { token: token ?? "", name, password } });
      await qc.invalidateQueries({ queryKey: getGetMeQueryKey() });
      toast({ title: "Welcome!", description: `You've joined ${inv?.companyName}.` });
      setLocation((result as { redirect?: string }).redirect || "/cp");
    } catch (err: unknown) {
      const errData = (err as { response?: { data?: { error?: string; needsSignup?: boolean } } })?.response?.data;
      if (errData?.needsSignup === false) {
        setUserExists(false);
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: errData?.error || "Failed to accept invitation.",
        });
      }
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950">
        <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
      </div>
    );
  }

  const errorMsg = invError
    ? ((invError as { response?: { data?: { error?: string } } })?.response?.data?.error ?? "Invalid or expired invitation.")
    : null;

  if (errorMsg || !inv) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 p-8">
        <div className="text-center max-w-sm">
          <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Invalid Invitation</h1>
          <p className="text-slate-400 mb-6">{errorMsg ?? "Invitation not found."}</p>
          <Button onClick={() => setLocation("/app/login")} className="bg-blue-600 hover:bg-blue-500">
            Back to Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-slate-950">
      <div className="hidden lg:flex w-[400px] flex-col bg-gradient-to-b from-blue-600 to-violet-800 p-12 relative overflow-hidden shrink-0 items-center justify-center">
        <div className="text-center relative z-10">
          <div className="w-20 h-20 bg-white/10 rounded-3xl flex items-center justify-center mx-auto mb-6">
            <Building2 className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-3">FinProject</h2>
          <p className="text-blue-100 text-sm">Construction Financial Management</p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-2xl bg-blue-600/20 flex items-center justify-center">
              <CheckCircle2 className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">You're invited!</h1>
              <p className="text-slate-400 text-sm">Join {inv.companyName} on FinProject</p>
            </div>
          </div>

          <div className="bg-slate-900 border border-slate-700 rounded-xl p-4 mb-8">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-slate-500 text-xs uppercase tracking-wider mb-1">Company</div>
                <div className="text-white font-medium">{inv.companyName}</div>
              </div>
              <div>
                <div className="text-slate-500 text-xs uppercase tracking-wider mb-1">Role</div>
                <div className="text-white font-medium">{inv.role}</div>
              </div>
              <div className="col-span-2">
                <div className="text-slate-500 text-xs uppercase tracking-wider mb-1">Email</div>
                <div className="text-white font-medium">{inv.email}</div>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {!userExists && (
              <>
                <div className="space-y-2">
                  <Label className="text-slate-300">Your full name</Label>
                  <Input
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder="Your Name"
                    required
                    className="h-12 rounded-xl bg-slate-900 border-slate-700 text-white placeholder:text-slate-600"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Create a password</Label>
                  <Input
                    type="password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="Min. 8 characters"
                    required
                    minLength={8}
                    className="h-12 rounded-xl bg-slate-900 border-slate-700 text-white placeholder:text-slate-600"
                  />
                </div>
              </>
            )}

            <Button
              type="submit"
              disabled={acceptInvite.isPending}
              className="w-full h-12 rounded-xl bg-gradient-to-r from-blue-600 to-violet-600 text-white font-semibold text-base border-0"
            >
              {acceptInvite.isPending
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Joining...</>
                : `Join ${inv.companyName}`
              }
            </Button>
          </form>

          <p className="text-center text-sm text-slate-500 mt-6">
            Already have an account?{" "}
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); setUserExists(true); }}
              className="text-blue-400 hover:text-blue-300 font-medium"
            >
              Sign in with existing account
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
